import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ScrollText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

/** Dismissible policy-update banner. Dismissal is stored per account, so it
 *  never comes back once closed. Purely informational — it never blocks the
 *  app and never records an acceptance. */
export default function LegalNoticeBanner() {
  const { user } = useAuth();
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    if (!user) { setNotices([]); return; }
    api.get("/legal/notices/active")
      .then(r => setNotices(r.data.notices || []))
      .catch(() => setNotices([]));
  }, [user]);

  const dismiss = async (id) => {
    setNotices(n => n.filter(x => x.id !== id));
    try { await api.post(`/legal/notices/${id}/dismiss`); } catch { /* best effort */ }
  };

  if (!user || notices.length === 0) return null;

  return (
    <div className="px-3 md:px-6 pt-3 space-y-2" data-testid="legal-notice-banners">
      {notices.map(n => (
        <div key={n.id}
          className="max-w-5xl mx-auto bg-[#2E6B57]/10 border border-[#2E6B57]/25 rounded-2xl px-4 py-3 flex items-start gap-3"
          data-testid={`legal-notice-${n.id}`}>
          <ScrollText size={17} className="text-[#245544] shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{n.title}</p>
            <p className="text-xs text-[#222]/70 mt-0.5">{n.body}</p>
            <Link to={n.link} className="inline-block text-xs font-semibold text-[#245544] mt-1.5"
              data-testid={`legal-notice-link-${n.id}`}>
              Read the updated policy →
            </Link>
          </div>
          <button onClick={() => dismiss(n.id)} aria-label="Dismiss notice"
            className="p-1 rounded-lg hover:bg-black/5 shrink-0"
            data-testid={`legal-notice-dismiss-${n.id}`}>
            <X size={15} />
          </button>
        </div>
      ))}
    </div>
  );
}
